import { useState, useEffect, useRef } from "react";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import Map from "./Map";
import { useTranslation } from 'react-i18next';
import { CleanUrl } from "../../../utils/cleanUrl";

function LocationMap(props) {
    const [mapError, setMapError] = useState("");
    const [geoLocation, setGeoLocation] = useState({ latitude: null, longitude: null });
    const [hasFetchedInitialLocation, setHasFetchedInitialLocation] = useState(false);
    const [geoLocationFetching, setGeoLocationFetching] = useState(false);
    const [geoLocationError, setGeoLocationError] = useState("");
    const [locationError, setLocationError] = useState("");
    const [locationsList, setLocationsList] = useState([]);
    const [mapInputValue, setMapInputValue] = useState("");
    const [mapSearchResults, setMapSearchResults] = useState([]);
    const [mapSearchPerformed, setMapSearchPerformed] = useState(false);
    const [locationInputValue, setLocationInputValue] = useState("");
    const [locationSearchResults, setLocationSearchResults] = useState([]);
    const [markers, setMarkers] = useState([]);
    const [activeMarkers, setActiveMarkers] = useState({});
    const [gmap, setGMap] = useState(null);
    const [activeSection, setActiveSection] = useState(null);
    const { register, resetField, trigger, promotionSettings, setValue, formState: { errors } } = props;
    const { t } = useTranslation();
    const API_KEY = promotionSettings.promotion_page.google_maps_api_key;
    const imagePath = CleanUrl(`${process.env.REACT_APP_BASE_URL}/${window.drupalDirectory}/`);
    const brandClass = "brand-joy"
    const markerPath = imagePath + "brand_markers/" + brandClass;
    const activeMarker = markerPath + "/pin-active.svg";
    const nonActiveMarker = markerPath + "/pin.svg";
    const locationList = promotionSettings.promotion_page.map_locations_list_json;
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [wasLocationSet, setWasLocationSet] = useState(false);

    const containerRef = useRef(null);
    const itemRefs = useRef([]);

    //admin configuration by default is map_optional = false for backwards compatibility
    const mapMandatory = !promotionSettings.promotion_page.store_selection_optional; 

    const country = promotionSettings.user.country;

    const clearLocationSelection = () => {
        setSelectedLocation(null);
        setValue('location_id', null);
        trigger('location_id');
        setMapSearchResults([]);
        setMapError("");
        clearMarkers();
        setActiveMarkers({});
    };

    const handleMapInputChange = (e) => {
        setGeoLocationError("");
        if (locationInputValue !== undefined && locationInputValue.length) {
            resetField("location_id");
            setLocationInputValue("");
            setLocationSearchResults([]);
        }

        setMapInputValue(e.target.value);

        if (e.target.value === "") {
            
            // unregister("map_location");
            setActiveSection(null);
            setMapError("");

            if (Object.entries(activeMarkers).length) {
                markers.filter(item => item.markerID === activeMarkers.markerID && item.setIcon(nonActiveMarker));
                activeMarkers.setIcon(activeMarker);
            }
            // setActiveMarkers({});
        };
        setMapSearchPerformed(false);
    }

    const handleOnEnter = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            clearLocationSelection();
            handleMapSearch();
        }
    }

    const handleMapSearch = async () => {
        if (mapInputValue === "") return
        if (markers.length) {
            markers.forEach(marker => marker.setMap(null));
            setMarkers([]);
        }
        clearLocationSelection();
        setActiveSection("part-1");
        setLocationError("");

        // setLocationsList()

        setMapSearchPerformed(true);
        handleGenerateShopArray(locationsList);
    }

    const handleGenerateShopArray = (list) => {
        // Normalize input for comparison
        const searchValue = mapInputValue.toLowerCase().trim();

        const shopsArray = list.filter(shop => {
            if (shop.name || shop.postal_code) {
                const nameMatch = shop.name && shop.name.toLowerCase().includes(searchValue);

                // Normalize postal code for comparison
                const postalCodeMatch = shop.postal_code && shop.postal_code.toLowerCase().replace(/\s+/g, ' ').includes(searchValue);

                return nameMatch || postalCodeMatch;
            } else {
                return false;
            }
        });

        if (!shopsArray.length) {
            setMapError(t("No results were found"));
            setMapSearchResults([]);
        } else {
            setMapError("");
            setMapSearchResults(shopsArray);
            shopsArray.forEach(item => createMarker(item));
        }
    };


    const handleLocationInputChange = async (e) => {
        setGeoLocationError("");
        if (mapInputValue.length) {
            resetField("map_location");
            setMapInputValue("");
            setMapError("");
        }

        if (activeMarkers.length) {
            setActiveMarkers({});
            markers.forEach(marker => marker.setIcon(promotionSettings.promotion_page.location_marker.non_active));
        }

        setActiveSection("part-2");

        setLocationInputValue(e.target.value);

        setLocationError("");
        setSelectedLocation(null);
        resetField("location_id");

        if (e.target.value === "") {
            setActiveSection(null);
            setLocationSearchResults([]);
            clearLocationSelection();
            return
        };

        

        const shopsArray = locationsList.filter((shop, i) => shop.name.toLowerCase().includes(e.target.value.toLowerCase().trim()) ? shop : null);

        if (!shopsArray.length && e.target.value !== "") {
            setLocationError(t("No location was found with this search value..."));
            setLocationSearchResults([]);
        } else {
            setLocationSearchResults(shopsArray);
        }
    }

    const emptyLocationInput = () => {
        setLocationError("");
        setLocationInputValue("");
        setLocationSearchResults([]);
        resetField("location");
        resetField("location_id");
        setActiveSection(null);
        clearLocationSelection();
        clearMarkers();
    }

    const emptyMapInput = () => {
        setMapInputValue("");
        setMapSearchResults([]);
        setMapError("");
        clearLocationSelection();
        setActiveSection(null);
        setMapSearchPerformed(false);
        clearMarkers();
        setActiveMarkers({});
    }

    const clearMarkers = () => {
        markers.forEach(marker => marker.setMap(null));
        setMarkers([]);
    }

    const handleChosenLocation = (value, id) => {
        setSelectedLocation(id);
        setLocationInputValue(value);
        setLocationSearchResults([]);

        if (Object.entries(activeMarkers).length) {
            activeMarkers.setIcon(promotionSettings.promotion_page.location_marker.non_active);
            setActiveMarkers({});
        }
    }

    const createMarker = (item) => {
        const position = { lat: parseFloat(item.lat), lng: parseFloat(item.lng) };

        const marker = new window.google.maps.Marker({
            position: position,
            map: gmap,
            icon: nonActiveMarker,
            markerID: item.id,
            optimized: false,
            zIndex: 1  // Initial zIndex
        });

        if (activeMarkers.markerID === marker.markerID) marker.setIcon(activeMarker);
        setMarkers((prevMarkers) => [...prevMarkers, marker]);

        marker.addListener('click', () => {
            handleMarkerClick(marker);
        });
    }

    useEffect(() => {
        if (markers.length > 0) {
            setBounds(markers);
        }
    }, [markers]);

    const handleMarkerClick = (marker) => {
        setActiveMarkers((prevState) => {
            setActiveSection("part-1");
            if (Object.entries(prevState).length) {
                prevState.setIcon(nonActiveMarker);
                prevState.setZIndex(1);  // Reset zIndex of previous active marker
            }
            marker.setIcon(activeMarker);
            marker.setZIndex(1000);  // Set zIndex of clicked marker
            gmap.setCenter(marker.getPosition());

            return marker;
        });

        setSelectedLocation(marker.markerID);

        const itemRef = document.getElementById(marker.markerID);
        if (itemRef) {
            itemRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const handleCheckboxChange = (event, id) => {
        const checkbox = event.target;
        if (activeMarkers.markerID) {
            const previousMarker = markers.find(marker => marker.markerID === activeMarkers.markerID);
            if (previousMarker) {
                previousMarker.setIcon(nonActiveMarker);
                previousMarker.setZIndex(1);  // Reset zIndex of previous active marker
            }
        }

        const targetMarker = markers.find(item => item.markerID === checkbox.id);
        if (targetMarker) {
            targetMarker.setIcon(activeMarker);
            targetMarker.setZIndex(1000);  // Set zIndex of clicked marker
            setActiveSection("part-1");
            setActiveMarkers(targetMarker);
            gmap.setCenter(targetMarker.getPosition());
            trigger("map_location");
        }

        setSelectedLocation(id);
    };

    const fetchGeolocation = () => {
        setMapInputValue("");
        setMapSearchPerformed(true);
        clearLocationSelection();
        setGeoLocationFetching(true);
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setGeoLocation({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                    });
                    setGeoLocationError(null);
                },
                (err) => {
                    setGeoLocationFetching(false);
                    setGeoLocationError(err.message);
                }
            );
        } else {
            setGeoLocationError(t("Geolocation is not supported by this browser"));
        }
    };

    useEffect(() => {
        if (!hasFetchedInitialLocation) {
            setHasFetchedInitialLocation(true);
            return;
        }

        const fetchURL = window.pathPrefix
            ? `${process.env.REACT_APP_BASE_URL}/${window.pathPrefix}/api/joypepsico-app/v1/promotion/${window.promotionID}/location?lat=${geoLocation.latitude}&long=${geoLocation.longitude}`
            : `${process.env.REACT_APP_BASE_URL}/api/joypepsico-app/v1/promotion/${window.promotionID}/location?lat=${geoLocation.latitude}&long=${geoLocation.longitude}`;


        const fetchData = async () => {
            setMapError("");

            try {
                const response = await fetch(fetchURL);
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const result = await response.json();
                // setLocationsList(result.result);
                setActiveSection("part-1");
                setLocationError("");
                handleGenerateShopArray(result.result);
            } catch (error) {
                // setError(error.message);
                setMapError(error.message);
            } finally {
                setGeoLocationFetching(false);
            }
        };

        fetchData();
    }, [geoLocation]);

    const render = (status) => {
        if (status === Status.LOADING) {
            return <p>Loading map...</p>;
        }
        if (status === Status.FAILED) {
            return <p className="error">Loading map failed. Try again later...</p>;
        }
        if (status === Status.SUCCESS) {
            return <Map setGMap={setGMap} />;
        }
    }

    useEffect(() => {
        if (gmap) {
            if (country) {
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ address: country }, (results, status) => {
                    if (status === "OK" && results[0]) {
                        const { lat, lng } = results[0].geometry.location;
                        gmap.setCenter({ lat: lat(), lng: lng() });
                        gmap.setZoom(6);
                    }
                });
            } else {
                gmap.setCenter({ lat: 50.5, lng: 10.5 });
                gmap.setZoom(4);
            }

        }
    }, [gmap, country]);

    useEffect(() => {
        if (Status.LOADING || Status.FAILED) {
            setActiveSection("part-2");
        }

        if (Status.SUCCESS) {
            setActiveSection(null);
            setLocationsList(locationList);
        }

        setLocationsList(locationList);
    }, [locationList]);

    useEffect(() => {
        resetField("map_location");
        resetField("location");
    }, [resetField]);

    useEffect(() => {
        if (markers.length > 0) {
            markers.forEach(marker => {
                marker.setIcon(nonActiveMarker);
                marker.setZIndex(1);  // Reset zIndex of all markers
            });
        }
    }, [markers, nonActiveMarker]);

    useEffect(() => {
        if (selectedLocation) {
            setWasLocationSet(true); // Mark as set when a non-empty value is detected
        }
        setValue("location_id", selectedLocation);
        trigger("location_id");
    }, [selectedLocation, setValue, trigger]);

    // implemented trigger to validate field properly on locationInputValue change 
    useEffect(() => {
        trigger('location_id');
    }, [locationInputValue]);

    const setBounds = (markersToRender) => {
        if (markersToRender.length === 0 || !gmap) return;

        const bounds = new window.google.maps.LatLngBounds();

        markersToRender.forEach(marker => {
            bounds.extend(marker.getPosition());
        });

        const padding = 50;

        gmap.fitBounds(bounds, padding);

        const currentZoom = gmap.getZoom();
        const adjustedZoom = Math.max(currentZoom - 2, 2);
        gmap.setZoom(adjustedZoom);
    };

    return (
        <>
            <div className="promo-fields-group promo-fields__border-bottom margin-bottom--40">
                <div className="promo-fields">
                    <div className="promo-field">
                        <div className="pweu-location">
                            {API_KEY && (
                                <div className={`location location--part-1 ${activeSection === "part-2" ? "disabled" : ""}`}>
                                    <h3 className="heading5 promo-fields-title">{t("Enter your location")}{mapMandatory && <>*</>}</h3>
                                    <div className="input-wrapper">
                                        <button type="button" disabled={activeSection === "part-2"} className="btn input-btn input-btn--left" onClick={fetchGeolocation}><span className="icon"><img src={imagePath + "geo-location.svg"} alt="Geo Location" /></span></button>
                                        <input type="text" value={mapInputValue} disabled={activeSection === "part-2"} onChange={(e) => handleMapInputChange(e)} name="city_location" placeholder={t("Enter location or ZIP code")} onKeyDown={handleOnEnter} />
                                        {!mapSearchPerformed && <button type="button" onClick={handleMapSearch} disabled={activeSection === "part-2"} className="btn input-btn input-btn--right"><span className="icon"><img src={imagePath + "search.svg"} alt="Search" /></span></button>}
                                        {mapSearchPerformed && <button type="button" onClick={emptyMapInput} className="btn input-btn input-btn--right"><span className="icon"><img src={imagePath + "delete.svg"} alt="Delete" /></span></button>}
                                    </div>
                                    <Wrapper apiKey={API_KEY} render={render} libraries={["geocoding"]} />
                                    {geoLocationFetching && <span className="geo-location-fetching">{t("Fetching location...")}</span>}
                                    {geoLocationError && <span className="error">{t(geoLocationError)}</span>}
                                    {mapError && <span className="error error-map" >{mapError}</span>}
                                    {mapSearchResults.length > 0 && <div className="search-results search-results--map" ref={containerRef}>
                                        {mapSearchResults.map((item, i) => (
                                            <div className="result-item" key={`${item.name}-${i}`} ref={(el) => (itemRefs.current[item.id] = el)}>
                                                <input type="radio" disabled={activeSection === "part-2"} checked={activeMarkers.markerID === item.id} name="map_location" value={item.id} id={item.id} onChange={(e) => handleCheckboxChange(e, item.id)} />
                                                <label htmlFor={item.id}>
                                                    <div>{item.name}</div>
                                                    <div>{item.street}, {item.postal_code} {item.city}</div>
                                                </label>
                                            </div>
                                        ))}
                                    </div>}
                                </div>
                            )}
                            <div className={`location location--part-2 ${activeSection === "part-1" ? "disabled" : ""}`}>
                                {API_KEY ? (<h3 className="heading5 promo-fields-title">{t("...or find your store / Online shop")}</h3>) : (<h3 className="heading5 promo-fields-title">{t("Find your store / Online Shop")}</h3>)}
                                <div className="input-wrapper">
                                    <input type="text" value={locationInputValue} disabled={activeSection === "part-1"} onChange={(e) => handleLocationInputChange(e)} autoComplete="off" name="shop_location" placeholder={t("Location name")} />
                                    {!locationInputValue && <button type="button" className="btn input-btn input-btn--right"><span className="icon"><img src={imagePath + "search.svg"} alt="Search" /></span></button>}
                                    {locationInputValue && <button type="button" onClick={emptyLocationInput} className="btn input-btn input-btn--right"><span className="icon icon"><img src={imagePath + "delete.svg"} alt="Delete" /></span></button>}
                                </div>
                                {locationError && <span className="error" >{locationError}</span>}
                                {locationSearchResults.length > 0 && <div className="search-results search-results--location">
                                    {locationSearchResults.map((item, i) => (
                                        <div className="result-item" key={`${item.name}-${i}`}>
                                            <input type="radio" name="manual_location" hidden value={item.id} id={`${item.id}-${i}`} onInput={() => handleChosenLocation(item.name, item.id)} />
                                            <label htmlFor={`${item.id}-${i}`}>
                                                {item.name.split(new RegExp(`(${locationInputValue})`, 'i')).map((part, index) => {
                                                    if (part.toLowerCase() === locationInputValue.toLowerCase()) {
                                                        return <strong key={`part-${index + 1}`}>{part}</strong>
                                                    } else {
                                                        return <span key={`part-${index + 1}`}>{part}</span>
                                                    }
                                                })}
                                            </label>
                                        </div>
                                    ))}
                                </div>}
                            </div>
                            <input
                                type="hidden"
                                {...register("location_id", {
                                    validate: (value) => {
                                        if (mapMandatory && !value) {
                                            return "Please select your location";
                                        }
                                        if(!mapMandatory) {
                                            if(locationInputValue !== 'undefined' && locationInputValue.length > 0) {
                                                if(!value) return false;
                                            }
                                        }
                                        return true;
                                    }
                                })}
                            />
                            {(errors.location_id && wasLocationSet) && <span className="error">{t(errors.location_id.message)}</span>}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default LocationMap;
